import {
  Subject,
  SyllabusUnit,
  Chapter,
  Topic,
  PastPaper,
  PaperTopicLink,
  StudySession,
  Task,
  RevisionEvent,
  Exam,
  Goal,
  StudentProfile,
  ChangeJournalEntry,
  SyncCheckpoint,
  ConflictRecord,
  findSyllabusUnitDuplicate,
} from '@study-tracker/domain';
import { IDatabase } from '../interfaces/IDatabase';
import { IRepository } from '../interfaces/IRepository';
import { IndexedDBRepository } from './IndexedDBRepository';
import { STORES } from './schema';
import { openNativeDB } from './nativeDb';
import { createDefaultSeedData } from '../seed/seedData';
import { BackupRecords } from '../interfaces/IDatabase';
import { ensureSyllabusCatalog } from '../seed/syllabusCatalog';

export class IndexedDBDatabase implements IDatabase {
  private dbPromise: Promise<IDBDatabase> | null = null;

  subjects!: IRepository<Subject>;
  syllabusUnits!: IRepository<SyllabusUnit>;
  chapters!: IRepository<Chapter>;
  topics!: IRepository<Topic>;
  pastPapers!: IRepository<PastPaper>;
  paperTopicLinks!: IRepository<PaperTopicLink>;
  studySessions!: IRepository<StudySession>;
  tasks!: IRepository<Task>;
  revisionEvents!: IRepository<RevisionEvent>;
  exams!: IRepository<Exam>;
  goals!: IRepository<Goal>;
  studentProfile!: IRepository<StudentProfile>;
  setSubjectArchivedStates!: IDatabase['setSubjectArchivedStates'];
  addSyllabusUnitIfUnique!: IDatabase['addSyllabusUnitIfUnique'];
  restoreRecordsAtomically!: IDatabase['restoreRecordsAtomically'];

  changeJournal!: IDatabase['changeJournal'];
  syncCheckpoints!: IDatabase['syncCheckpoints'];
  conflictRecords!: IDatabase['conflictRecords'];

  constructor() {
    const getDb = () => this.getDB();

    this.subjects = new IndexedDBRepository<Subject>(getDb, STORES.subjects);
    this.syllabusUnits = new IndexedDBRepository<SyllabusUnit>(getDb, STORES.syllabusUnits);
    this.chapters = new IndexedDBRepository<Chapter>(getDb, STORES.chapters);
    this.topics = new IndexedDBRepository<Topic>(getDb, STORES.topics);
    this.pastPapers = new IndexedDBRepository<PastPaper>(getDb, STORES.pastPapers);
    this.paperTopicLinks = new IndexedDBRepository<PaperTopicLink>(getDb, STORES.paperTopicLinks);
    this.studySessions = new IndexedDBRepository<StudySession>(getDb, STORES.studySessions);
    this.tasks = new IndexedDBRepository<Task>(getDb, STORES.tasks);
    this.revisionEvents = new IndexedDBRepository<RevisionEvent>(getDb, STORES.revisionEvents);
    this.exams = new IndexedDBRepository<Exam>(getDb, STORES.exams);
    this.goals = new IndexedDBRepository<Goal>(getDb, STORES.goals);
    this.studentProfile = new IndexedDBRepository<StudentProfile>(getDb, STORES.studentProfile);
    this.addSyllabusUnitIfUnique = async (unit) => {
      const db = await getDb();
      const tx = db.transaction(STORES.syllabusUnits, 'readwrite');
      const store = tx.objectStore(STORES.syllabusUnits);
      let result: 'created' | 'duplicate-title' | 'duplicate-number' = 'created';
      const request = store.getAll();
      request.onsuccess = () => {
        const existing = (request.result as SyllabusUnit[]).filter((record) => record.deletedAt === null && record.subjectId === unit.subjectId && record.id !== unit.id);
        if (findSyllabusUnitDuplicate(existing, unit) === 'title') {
          result = 'duplicate-title';
          return;
        }
        if (findSyllabusUnitDuplicate(existing, unit) === 'number') {
          result = 'duplicate-number';
          return;
        }
        store.put(unit);
      };
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onabort = () => reject(tx.error || new Error('Adding syllabus unit was aborted.'));
        tx.onerror = () => reject(tx.error || new Error('Adding syllabus unit failed.'));
      });
      return result;
    };
    this.setSubjectArchivedStates = async (states) => {
      const db = await getDb();
      const tx = db.transaction(STORES.subjects, 'readwrite');
      const store = tx.objectStore(STORES.subjects);
      for (const state of states) {
        const request = store.get(state.id);
        request.onsuccess = () => {
          const subject = request.result as Subject | undefined;
          if (subject) store.put({
            ...subject,
            isArchived: state.isArchived,
            updatedAt: new Date().toISOString(),
            version: subject.version + 1,
          });
        };
      }
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onabort = () => reject(tx.error || new Error('Subject switch transaction was aborted.'));
        tx.onerror = () => reject(tx.error || new Error('Subject switch transaction failed.'));
      });
    };
    this.restoreRecordsAtomically = async (records: BackupRecords) => {
      const db = await getDb();
      const stores = [
        STORES.subjects, STORES.syllabusUnits, STORES.chapters, STORES.topics,
        STORES.pastPapers, STORES.paperTopicLinks, STORES.studySessions, STORES.tasks,
        STORES.revisionEvents, STORES.exams, STORES.goals, STORES.studentProfile,
      ];
      const tx = db.transaction(stores, 'readwrite');
      for (const storeName of stores) {
        const storeRecords = records[storeName as keyof BackupRecords];
        const store = tx.objectStore(storeName);
        for (const record of storeRecords) store.put(record);
      }
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onabort = () => reject(tx.error || new Error('Backup restore transaction was aborted.'));
        tx.onerror = () => reject(tx.error || new Error('Backup restore transaction failed.'));
      });
    };

    // Sync primitives
    this.changeJournal = {
      append: async (entry: ChangeJournalEntry) => {
        const db = await getDb();
        const tx = db.transaction(STORES.changeJournal, 'readwrite');
        tx.objectStore(STORES.changeJournal).put(entry);
      },
      findSince: async (sequenceNumber: number) => {
        const db = await getDb();
        const tx = db.transaction(STORES.changeJournal, 'readonly');
        return new Promise<ChangeJournalEntry[]>((resolve, reject) => {
          const req = tx.objectStore(STORES.changeJournal).getAll();
          req.onsuccess = () => {
            const all = req.result as ChangeJournalEntry[];
            resolve(
              all
                .filter((e) => e.sequenceNumber > sequenceNumber)
                .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
            );
          };
          req.onerror = () => reject(req.error);
        });
      },
      getLatestSequence: async () => {
        const db = await getDb();
        const tx = db.transaction(STORES.changeJournal, 'readonly');
        return new Promise<number>((resolve, reject) => {
          const req = tx.objectStore(STORES.changeJournal).getAll();
          req.onsuccess = () => {
            const all = req.result as ChangeJournalEntry[];
            if (all.length === 0) return resolve(0);
            resolve(Math.max(...all.map((e) => e.sequenceNumber)));
          };
          req.onerror = () => reject(req.error);
        });
      },
      clear: async () => {
        const db = await getDb();
        const tx = db.transaction(STORES.changeJournal, 'readwrite');
        tx.objectStore(STORES.changeJournal).clear();
      },
    };

    this.syncCheckpoints = {
      getByDeviceId: async (deviceId: string) => {
        const db = await getDb();
        const tx = db.transaction(STORES.syncCheckpoints, 'readonly');
        return new Promise<SyncCheckpoint | null>((resolve, reject) => {
          const req = tx.objectStore(STORES.syncCheckpoints).get(deviceId);
          req.onsuccess = () => resolve((req.result as SyncCheckpoint) || null);
          req.onerror = () => reject(req.error);
        });
      },
      upsert: async (checkpoint: SyncCheckpoint) => {
        const db = await getDb();
        const tx = db.transaction(STORES.syncCheckpoints, 'readwrite');
        tx.objectStore(STORES.syncCheckpoints).put(checkpoint);
      },
      getAll: async () => {
        const db = await getDb();
        const tx = db.transaction(STORES.syncCheckpoints, 'readonly');
        return new Promise<SyncCheckpoint[]>((resolve, reject) => {
          const req = tx.objectStore(STORES.syncCheckpoints).getAll();
          req.onsuccess = () => resolve(req.result as SyncCheckpoint[]);
          req.onerror = () => reject(req.error);
        });
      },
    };

    this.conflictRecords = {
      append: async (record: ConflictRecord) => {
        const db = await getDb();
        const tx = db.transaction(STORES.conflictRecords, 'readwrite');
        tx.objectStore(STORES.conflictRecords).put(record);
      },
      getAll: async () => {
        const db = await getDb();
        const tx = db.transaction(STORES.conflictRecords, 'readonly');
        return new Promise<ConflictRecord[]>((resolve, reject) => {
          const req = tx.objectStore(STORES.conflictRecords).getAll();
          req.onsuccess = () => resolve(req.result as ConflictRecord[]);
          req.onerror = () => reject(req.error);
        });
      },
      clear: async () => {
        const db = await getDb();
        const tx = db.transaction(STORES.conflictRecords, 'readwrite');
        tx.objectStore(STORES.conflictRecords).clear();
      },
    };
  }

  private async getDB(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openNativeDB();

      // Request browser storage persistence
      if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.persist) {
        void navigator.storage.persist().catch((error: unknown) => {
          console.warn('Persistent browser storage could not be requested:', error);
        });
      }

      try {
        const db = await this.dbPromise;
        const tx = db.transaction(STORES.subjects, 'readonly');
        const countReq = tx.objectStore(STORES.subjects).count();
        const subjectCount = await new Promise<number>((resolve, reject) => {
          countReq.onsuccess = () => resolve(countReq.result);
          countReq.onerror = () => reject(countReq.error || new Error('Failed to count subjects in local database.'));
        });
        if (subjectCount === 0) await this.seedInitialData();
        await ensureSyllabusCatalog(this);
      } catch (error) {
        const failedDatabase = await this.dbPromise;
        failedDatabase.close();
        this.dbPromise = null;
        throw error;
      }
    }
    return this.dbPromise;
  }

  private async seedInitialData(): Promise<void> {
    const seed = createDefaultSeedData();
    await this.subjects.upsertBatch(seed.subjects);
    await this.syllabusUnits.upsertBatch(seed.syllabusUnits);
    await this.chapters.upsertBatch(seed.chapters);
    await this.topics.upsertBatch(seed.topics);
    await this.pastPapers.upsertBatch(seed.pastPapers);
    await this.studentProfile.upsert(seed.studentProfile);
    await this.goals.upsert(seed.goal);
  }

  async close(): Promise<void> {
    if (this.dbPromise) {
      const db = await this.dbPromise;
      db.close();
      this.dbPromise = null;
    }
  }

  async clearAll(): Promise<void> {
    const db = await this.getDB();
    const names = Array.from(db.objectStoreNames);
    const tx = db.transaction(names, 'readwrite');
    for (const name of names) {
      tx.objectStore(name).clear();
    }
  }
}
